import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  ArrowLeft,
  Menu,
  ShoppingCart,
  X,
  User,
  CalendarDays,
} from "lucide-react";

import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

const VISITCAMSUR_URL = "https://book.visitcamsur.com/";
const NAVBAR_LOGO_SRC = "/newassets/logo%201.png";

const NAV_LINKS = [
  { label: "Home", id: "home" },
  { label: "What's New", id: "whats-new" },
  { label: "Gallery", id: "gallery" },
  { label: "Facility", id: "facility" },
  { label: "Rates", id: "rates" },
  { label: "Shop", id: "addons" },
  { label: "Court Rules", id: "court-rules" },
  { label: "Location", id: "location" },
];

/** Section ids whose background is light → navbar text should be dark for contrast */
const LIGHT_SECTION_IDS = new Set([
  "gallery",
  "facility",
  "rates",
  "addons",
  "location",
]);

/** Compact nav link — sized so all links + actions fit on a single row. Text color is inherited so it flips with the navbar tone. */
const desktopNavClass = (overLight) =>
  `whitespace-nowrap rounded-md px-2 py-1.5 text-[13px] font-bold uppercase tracking-tight transition-all duration-200 hover:scale-105 hover:shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#c7e150] xl:px-2.5 xl:text-sm ${overLight ? "hover:bg-black/10" : "hover:bg-white/20"}`;

const actionBtnClass =
  "inline-flex min-h-[40px] items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-[13px] font-bold uppercase tracking-tight transition-all duration-200 hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white xl:px-4 xl:text-sm";

export default function PublicNavbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [overLight, setOverLight] = useState(false);
  const { user, logout } = useAuth();
  const { getItemCount, openCart } = useCart();
  const navigate = useNavigate();

  const cartCount = getItemCount();

  // Flip navbar text to dark when the section sitting under the bar has a light background
  useEffect(() => {
    let frame = null;

    const evaluate = () => {
      frame = null;
      const navEl = document.getElementById("public-navbar");
      const sampleY = (navEl?.offsetHeight ?? 64) + 8;
      let isLight = false;

      for (const { id } of NAV_LINKS) {
        const section = document.getElementById(id);
        if (!section) continue;
        const rect = section.getBoundingClientRect();
        if (rect.top <= sampleY && rect.bottom > sampleY) {
          isLight = LIGHT_SECTION_IDS.has(id);
          break;
        }
      }

      setOverLight(isLight);
    };

    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(evaluate);
    };

    evaluate();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);

    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  const scrollToSection = useCallback((sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    setMenuOpen(false);
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuOpen]);

  return (
    <>
      <nav
        id="public-navbar"
        className={`fixed left-0 right-0 top-0 z-50 transition-colors duration-300 ${
          overLight
            ? "text-[#10233f]"
            : "text-white [text-shadow:0_1px_4px_rgba(0,0,0,0.55)]"
        }`}
        aria-label="Main navigation"
      >
        <div>
          <div className="mx-auto flex min-h-[64px] max-w-screen-2xl items-center gap-2 px-3 sm:px-4 lg:px-5">
            <Link
              to="/"
              className="flex shrink-0 items-center"
              aria-label="CamSur Pickleball Club home"
              onClick={() => setMenuOpen(false)}
            >
              <img
                src={NAVBAR_LOGO_SRC}
                alt="CamSur Pickleball Club"
                width={120}
                height={140}
                decoding="async"
                loading="eager"
                className="mt-2 h-16 w-auto max-w-none object-contain object-center sm:h-20 [image-rendering:auto] [-webkit-backface-visibility:hidden] [backface-visibility:hidden] [transform:translateZ(0)]"
                style={{
                  filter: "drop-shadow(0 0 0.75px rgba(255,255,255,0.12))",
                }}
              />
            </Link>

            {/* Nav links — centered, single line (lg+) */}
            <div className="hidden min-w-0 flex-1 items-center justify-center lg:flex">
              <div className="flex flex-nowrap items-center gap-0.5 xl:gap-1">
                {NAV_LINKS.map(({ label, id }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => scrollToSection(id)}
                    className={desktopNavClass(overLight)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className="ml-auto flex shrink-0 items-center gap-1.5 lg:ml-0">
              <a
                href={VISITCAMSUR_URL}
                aria-label="Back to VisitCamSur"
                className={`group hidden items-center gap-2 overflow-hidden rounded-full px-4 py-2 text-[13px] font-bold uppercase tracking-widest backdrop-blur-sm transition-all duration-300 active:scale-95 sm:inline-flex xl:px-5 xl:text-sm ${
                  overLight
                    ? "bg-black/10 text-[#10233f] hover:bg-black/20 hover:shadow-[0_0_16px_rgba(0,0,0,0.15)]"
                    : "bg-white/15 text-white hover:bg-white/25 hover:shadow-[0_0_16px_rgba(199,225,80,0.35)]"
                }`}
              >
                <ArrowLeft className="h-3.5 w-3.5 shrink-0 transition-transform duration-300 group-hover:-translate-x-0.5" />
                <span className="hidden xl:inline">VisitCamSur</span>
                <span className="xl:hidden">CamSur</span>
              </a>

              <Link
                to="/book/venue_camsur"
                className={`${actionBtnClass} hidden bg-[#f48a15] text-white hover:bg-[#e07d0f] md:inline-flex`}
              >
                <CalendarDays className="h-4 w-4 shrink-0" />
                Book
              </Link>

              {user && (
                <button
                  type="button"
                  onClick={openCart}
                  className={`relative flex min-h-[40px] min-w-[40px] items-center justify-center rounded-full transition hover:scale-105 ${overLight ? "hover:bg-black/10" : "hover:bg-white/15"}`}
                  aria-label="Open cart"
                >
                  <ShoppingCart className="h-6 w-6" />
                  {cartCount > 0 && (
                    <span className="absolute right-0 top-0 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold text-white shadow-sm">
                      {cartCount > 99 ? "99+" : cartCount}
                    </span>
                  )}
                </button>
              )}

              {!user ? (
                <Link
                  to="/login"
                  className={`${actionBtnClass} hidden border-2 lg:inline-flex ${overLight ? "border-[#10233f]/40 hover:bg-black/5" : "border-white/30 hover:bg-white/10"}`}
                >
                  <User className="h-4 w-4" />
                  Log in
                </Link>
              ) : (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`hidden h-10 min-h-[40px] gap-2 rounded-full px-2 lg:inline-flex ${overLight ? "hover:bg-black/10" : "hover:bg-white/10"}`}
                    >
                      <Avatar className="h-8 w-8 bg-[#f48a15]">
                        <AvatarImage src={user?.picture} />
                        <AvatarFallback className="text-sm font-bold">
                          {user?.name?.charAt(0)?.toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <span className="max-w-[100px] truncate text-sm font-bold">
                        {user?.name?.split(" ")[0]}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52 text-base">
                    <DropdownMenuItem
                      className="py-3 text-base font-medium"
                      onClick={() => navigate("/profile")}
                    >
                      Profile
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="py-3 text-base font-medium"
                      onClick={() => navigate("/my-bookings")}
                    >
                      My Bookings
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="py-3 text-base font-medium text-destructive"
                      onClick={logout}
                    >
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Hamburger — shown below lg */}
              <button
                type="button"
                className={`inline-flex min-h-[48px] min-w-[48px] items-center justify-center gap-2 rounded-xl border-2 px-3 transition-all duration-200 hover:scale-105 hover:border-[#c7e150] lg:hidden ${overLight ? "border-[#10233f]/30 bg-black/5 hover:bg-black/10" : "border-white/25 bg-white/10 hover:bg-white/20"}`}
                aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={menuOpen}
                aria-controls="mobile-nav-panel"
                onClick={() => setMenuOpen((open) => !open)}
              >
                {menuOpen ? (
                  <>
                    <X className="h-7 w-7 shrink-0" aria-hidden="true" />
                    <span className="text-sm font-bold uppercase">Close</span>
                  </>
                ) : (
                  <>
                    <Menu className="h-7 w-7 shrink-0" aria-hidden="true" />
                    <span className="text-sm font-bold uppercase">Menu</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile hamburger drawer */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden"
          role="dialog"
          aria-modal="true"
          aria-label="Site navigation"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            aria-label="Close menu"
            onClick={() => setMenuOpen(false)}
          />

          <div
            id="mobile-nav-panel"
            className="absolute left-0 right-0 top-[60px] max-h-[calc(100vh-60px)] overflow-y-auto border-b-4 border-[#c7e150] bg-[#1e4b85] shadow-2xl"
          >
            <div className="mx-auto max-w-lg px-4 py-5 sm:px-6">
              <p className="mb-4 text-center text-base font-bold uppercase tracking-wide text-[#c7e150]">
                Navigate
              </p>

              {/* Primary actions — always visible in mobile menu */}
              <div className="mb-5 flex flex-col gap-3">
                <a
                  href={VISITCAMSUR_URL}
                  className="flex min-h-[52px] items-center justify-center gap-3 rounded-xl bg-[#c7e150] px-4 text-base font-bold uppercase tracking-wide text-[#1e4b85] shadow-[3px_3px_0px_#163a66] transition hover:scale-[1.02] active:scale-[0.98]"
                >
                  <ArrowLeft className="h-5 w-5" />
                  Back to VisitCamSur
                </a>
                <Link
                  to="/book/venue_camsur"
                  onClick={() => setMenuOpen(false)}
                  className="flex min-h-[52px] items-center justify-center gap-3 rounded-xl bg-[#f48a15] px-4 text-base font-bold uppercase tracking-wide text-white transition hover:scale-[1.02] active:scale-[0.98]"
                >
                  <CalendarDays className="h-5 w-5" />
                  Book a court
                </Link>
              </div>

              {/* Section links — large tap targets */}
              <ul className="flex flex-col gap-2">
                {NAV_LINKS.map(({ label, id }) => (
                  <li key={id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(id)}
                      className="flex min-h-[52px] w-full items-center rounded-xl border-2 border-white/15 bg-white/5 px-5 text-left text-base font-bold uppercase tracking-wide text-white transition-all duration-200 hover:scale-[1.01] hover:border-[#c7e150] hover:bg-white/15 hover:text-lg active:scale-[0.99]"
                    >
                      {label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Auth */}
              <div className="mt-6 border-t-2 border-white/15 pt-5">
                {!user ? (
                  <Link
                    to="/login"
                    onClick={() => setMenuOpen(false)}
                    className="flex min-h-[52px] w-full items-center justify-center gap-3 rounded-xl border-2 border-white/30 text-base font-bold uppercase tracking-wide text-white transition hover:bg-white/10 hover:text-lg"
                  >
                    <User className="h-5 w-5" />
                    Log in / Sign up
                  </Link>
                ) : (
                  <div className="flex flex-col gap-2">
                    {user && (
                      <button
                        type="button"
                        onClick={() => {
                          openCart();
                          setMenuOpen(false);
                        }}
                        className="flex min-h-[52px] items-center gap-3 rounded-xl px-5 text-base font-bold text-white transition hover:bg-white/10 hover:text-lg"
                      >
                        <ShoppingCart className="h-5 w-5" />
                        Cart{cartCount > 0 ? ` (${cartCount})` : ""}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/profile");
                        setMenuOpen(false);
                      }}
                      className="flex min-h-[48px] items-center rounded-xl px-5 text-left text-base font-semibold text-white hover:bg-white/10 hover:text-lg"
                    >
                      Profile
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/my-bookings");
                        setMenuOpen(false);
                      }}
                      className="flex min-h-[48px] items-center rounded-xl px-5 text-left text-base font-semibold text-white hover:bg-white/10 hover:text-lg"
                    >
                      My Bookings
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        logout();
                        setMenuOpen(false);
                      }}
                      className="flex min-h-[48px] items-center rounded-xl px-5 text-left text-base font-semibold text-red-300 hover:bg-white/5 hover:text-lg"
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
